import React, { type FunctionComponent } from 'react';
import {
    Button,
    Input,
    InputGroup,
    InputLeftAddon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Select,
} from '@chakra-ui/react';
import type { Translation } from '../../interface/Localization';

interface ProjectOption {
    id: string;
    name: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    projectOptions: ProjectOption[];
    selectedProjectId: string;
    onProjectChange: (projectId: string) => void;
    newTranslationKey: string;
    onKeyChange: (key: string) => void;
    newTranslationDrafts: Translation[];
    onValueChange: (language: string, value: string) => void;
    onSave: () => void;
    isSaving: boolean;
}

export const CreateTranslationModal: FunctionComponent<Props> = ({
    isOpen,
    onClose,
    projectOptions,
    selectedProjectId,
    onProjectChange,
    newTranslationKey,
    onKeyChange,
    newTranslationDrafts,
    onValueChange,
    onSave,
    isSaving,
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Create new translation</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <InputGroup marginBottom={4}>
                        <InputLeftAddon>Project</InputLeftAddon>
                        <Select
                            value={selectedProjectId}
                            onChange={(event) => {
                                onProjectChange(event.target.value);
                            }}
                        >
                            {projectOptions.map((project) => (
                                <option value={project.id} key={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </Select>
                    </InputGroup>
                    <InputGroup marginBottom={4}>
                        <InputLeftAddon width={55}>Key</InputLeftAddon>
                        <Input
                            value={newTranslationKey}
                            placeholder="Enter translation key"
                            onChange={(event) => {
                                onKeyChange(event.target.value);
                            }}
                        />
                    </InputGroup>

                    {newTranslationDrafts.map((translation) => (
                        <InputGroup key={`new-translation-${translation.language}`} marginBottom={2}>
                            <InputLeftAddon>{translation.language_code.toUpperCase()}</InputLeftAddon>
                            <Input
                                value={translation.value ?? ''}
                                placeholder=""
                                onChange={(event) => {
                                    onValueChange(translation.language, event.target.value);
                                }}
                            />
                        </InputGroup>
                    ))}
                </ModalBody>
                <ModalFooter>
                    <Button mr={3} variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={onSave}
                        isLoading={isSaving}
                        isDisabled={!newTranslationKey.trim() || !selectedProjectId}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
